/**
 * Процедурная фоновая музыка (Web Audio) в духе gearSound.ts.
 *
 * Бесконечный спокойный эмбиент-луп без внешних файлов: пэд-аккорды + мягкое
 * арпеджио + бас по прогрессии vi–IV–I–V. Громкость низкая, чтобы не мешать
 * звуковым эффектам. Планирование нот — через lookahead-планировщик (классический
 * паттерн Web Audio): setInterval будит планировщик, ноты ставятся в очередь
 * AudioContext заранее, поэтому ритм не зависит от джиттера таймера.
 */

import { type AudioTheme, buildChords, waveGain } from './audioThemes.js';
import { bindAudioLifecycle } from './audioLifecycle.js';

const A4 = 440;

/** MIDI-нота → частота (Гц). */
export function midiToFreq(midi: number): number {
  return A4 * 2 ** ((midi - 69) / 12);
}

export interface Chord {
  /** MIDI корня в низком регистре (бас). */
  bass: number;
  /** MIDI ноты аккорда для пэда и арпеджио (по возрастанию). */
  notes: number[];
}

/** Прогрессия vi–IV–I–V в C-мажоре: Am · F · C · G. */
export const PROGRESSION: Chord[] = [
  { bass: 45, notes: [57, 60, 64, 69] }, // Am: A2 ; A3 C4 E4 A4
  { bass: 41, notes: [53, 57, 60, 65] }, // F:  F2 ; F3 A3 C4 F4
  { bass: 48, notes: [55, 60, 64, 67] }, // C:  C3 ; G3 C4 E4 G4
  { bass: 43, notes: [55, 59, 62, 67] }, // G:  G2 ; G3 B3 D4 G4
];

/** Шагов (восьмых) на один аккорд. */
const STEPS_PER_CHORD = 8;

/** Паттерн арпеджио — индексы в Chord.notes, проигрываемые по шагам. */
const ARP_PATTERN = [0, 2, 1, 3, 2, 1, 2, 0];

/** Возвращает аккорд прогрессии для глобального номера шага. */
export function chordForStep(step: number): Chord {
  const index = Math.floor(step / STEPS_PER_CHORD) % PROGRESSION.length;
  return PROGRESSION[index]!;
}

export class BackgroundMusicPlayer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private enabled: boolean;
  private playing = false;
  private schedulerId: ReturnType<typeof setInterval> | null = null;
  private nextStepTime = 0;
  private step = 0;
  private scheduled = new Set<OscillatorNode>();
  /** Тема стиля уровня; null — исходный спокойный луп Am–F–C–G. */
  private theme: AudioTheme | null = null;
  private chords: Chord[] = PROGRESSION;

  private stepDur = 0.42; // длительность восьмой (сек) ≈ 71 BPM; тема меняет
  private readonly lookahead = 0.2; // окно планирования (сек)
  private readonly tickMs = 40; // период тика планировщика
  private readonly volume = 0.64; // целевая громкость master (×4 от исходной 0.16)

  constructor(enabled = true) {
    this.enabled = enabled;
    // тишина, пока страница в фоне (другое приложение, свёрнут браузер, экран заблокирован)
    bindAudioLifecycle(
      () => this.ctx,
      () => this.enabled,
    );
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Переключает музыку на тему стиля; если играет — плавно перезапускает в новой теме. */
  setTheme(theme: AudioTheme | null): void {
    if (theme === this.theme) return;
    this.theme = theme;
    this.chords = theme ? buildChords(theme) : PROGRESSION;
    this.stepDur = theme ? theme.stepSec : 0.42;
    if (this.playing) {
      this.stop();
      this.start();
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      this.resume();
    } else {
      this.stop();
    }
  }

  /** Вызвать на первом пользовательском жесте: разблокирует Web Audio и стартует. */
  resume(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    // 'interrupted' — iOS после блокировки/звонка: тоже нужен resume() по жесту
    if (ctx.state !== 'running') {
      void ctx.resume().then(() => this.start());
    }
    this.start();
  }

  start(): void {
    if (!this.enabled || this.playing) return;
    const ctx = this.getContext();
    if (ctx.state !== 'running') return; // стартуем из resume() после разблокировки

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, ctx.currentTime);
    master.gain.linearRampToValueAtTime(this.volume * (this.theme?.musicGain ?? 1), ctx.currentTime + 2.5);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = this.theme?.filterHz ?? 2200;
    filter.Q.value = 0.6;
    master.connect(filter);
    filter.connect(ctx.destination);
    this.master = master;
    this.filter = filter;

    this.playing = true;
    this.step = 0;
    this.nextStepTime = ctx.currentTime + 0.1;
    this.schedulerId = setInterval(() => this.scheduler(), this.tickMs);
  }

  stop(): void {
    if (this.schedulerId !== null) {
      clearInterval(this.schedulerId);
      this.schedulerId = null;
    }
    this.playing = false;

    const ctx = this.ctx;
    const master = this.master;
    const filter = this.filter;
    this.master = null;
    this.filter = null;
    if (!ctx || !master) return;

    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0.0001, now + 0.5);
    for (const osc of this.scheduled) {
      try {
        osc.stop(now + 0.6);
      } catch {
        // уже остановлен
      }
    }
    this.scheduled.clear();
    setTimeout(() => {
      try {
        master.disconnect();
        filter?.disconnect();
      } catch {
        // узлы уже отключены
      }
    }, 800);
  }

  private getContext(): AudioContext {
    if (!this.ctx) {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.ctx = new Ctx();
    }
    return this.ctx;
  }

  private scheduler(): void {
    const ctx = this.ctx;
    if (!ctx || !this.playing) return;
    while (this.nextStepTime < ctx.currentTime + this.lookahead) {
      this.scheduleStep(this.step, this.nextStepTime);
      this.nextStepTime += this.stepDur;
      this.step += 1;
    }
  }

  private scheduleStep(step: number, time: number): void {
    const chord = this.chords[Math.floor(step / STEPS_PER_CHORD) % this.chords.length]!;
    const localStep = step % STEPS_PER_CHORD;

    // На границе аккорда — пэд (верхние три ноты) и бас.
    if (localStep === 0) {
      const chordDur = STEPS_PER_CHORD * this.stepDur;
      for (const note of chord.notes.slice(1)) {
        this.schedulePadVoice(note, time, chordDur);
      }
      this.scheduleBass(chord.bass, time, chordDur);
    }

    // Арпеджио — каждый шаг, на октаву выше для звонкости.
    const pattern = this.theme?.arpPattern ?? ARP_PATTERN;
    const arpNote =
      chord.notes[pattern[step % pattern.length]!]! + (this.theme ? this.theme.arpOctave * 12 : 12);
    this.scheduleArp(arpNote, time);
  }

  private schedulePadVoice(midi: number, time: number, dur: number): void {
    const ctx = this.ctx!;
    const master = this.master;
    if (!master) return;

    const osc = ctx.createOscillator();
    osc.type = this.theme?.padWave ?? 'sine';
    osc.frequency.value = midiToFreq(midi);

    const gain = ctx.createGain();
    const peak = 0.05 * waveGain(this.theme?.padWave ?? 'sine');
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(peak, time + 0.8); // мягкая атака
    gain.gain.setValueAtTime(peak, time + dur);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + dur + 1.0); // длинный релиз

    osc.connect(gain);
    gain.connect(master);
    this.track(osc, time, dur + 1.1);
  }

  private scheduleBass(midi: number, time: number, dur: number): void {
    const ctx = this.ctx!;
    const master = this.master;
    if (!master) return;

    const osc = ctx.createOscillator();
    osc.type = this.theme?.bassWave ?? 'sine';
    osc.frequency.value = midiToFreq(midi);

    const gain = ctx.createGain();
    const peak = 0.13 * waveGain(this.theme?.bassWave ?? 'sine');
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(peak, time + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + dur * 0.95);

    osc.connect(gain);
    gain.connect(master);
    this.track(osc, time, dur);
  }

  private scheduleArp(midi: number, time: number): void {
    const ctx = this.ctx!;
    const master = this.master;
    if (!master) return;

    const osc = ctx.createOscillator();
    osc.type = this.theme?.arpWave ?? 'triangle';
    osc.frequency.value = midiToFreq(midi);

    const gain = ctx.createGain();
    const peak = 0.045 * waveGain(this.theme?.arpWave ?? 'triangle');
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(peak, time + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.5);

    osc.connect(gain);
    gain.connect(master);
    this.track(osc, time, 0.55);
  }

  /** Запускает осциллятор, планирует его остановку и учёт для остановки музыки. */
  private track(osc: OscillatorNode, time: number, life: number): void {
    osc.onended = () => {
      this.scheduled.delete(osc);
    };
    this.scheduled.add(osc);
    osc.start(time);
    osc.stop(time + life);
  }
}
